import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import SearchablePicker from "@/components/SearchablePicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { GitMerge, Warning, ArrowRight, User, Trash } from "@phosphor-icons/react";

const ROLE_LABEL = {
  admin: "Gérant",
  compta: "Compta",
  associate: "Associé",
  manager: "Manager",
  technician: "Technicien",
};

function UserCard({ user, isPrimary }) {
  if (!user) {
    return (
      <div className="flex-1 rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-4 text-center text-xs text-white/40">
        Sélectionne un utilisateur
      </div>
    );
  }
  return (
    <div
      className={`flex-1 rounded-xl border p-3 ${
        isPrimary
          ? "bg-emerald-500/10 border-emerald-500/40"
          : "bg-red-500/10 border-red-500/40"
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm truncate">{user.name}</div>
          <div className="text-[10px] text-white/60">{ROLE_LABEL[user.role] || user.role}</div>
        </div>
        <span
          className={`shrink-0 text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
            isPrimary ? "bg-emerald-500 text-black" : "bg-red-500 text-white"
          }`}
        >
          {isPrimary ? "Gardé" : "Supprimé"}
        </span>
      </div>
      <div className="space-y-0.5 text-[11px] text-white/60">
        {user.email && <div className="truncate">✉️ {user.email}</div>}
        {user.phone && <div>📞 {user.phone}</div>}
      </div>
    </div>
  );
}

export default function UserMergePanel() {
  const [users, setUsers] = useState([]);
  const [primaryId, setPrimaryId] = useState("");
  const [secondaryId, setSecondaryId] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  useEffect(() => { load(); }, []);

  const primary = users.find((u) => u.id === primaryId) || null;
  const secondary = users.find((u) => u.id === secondaryId) || null;
  const sameUser = primaryId && primaryId === secondaryId;
  const canMerge = primary && secondary && !sameUser;

  // Prevent selecting the same user in both dropdowns
  const primaryOptions = users.filter((u) => u.id !== secondaryId);
  const secondaryOptions = users.filter((u) => u.id !== primaryId);

  const doMerge = async () => {
    setMerging(true);
    try {
      const r = await api.post("/admin/users/merge", {
        primary_id: primaryId,
        secondary_id: secondaryId,
      });
      setResult(r.data);
      toast.success(`Fusion réussie — ${secondary.name} → ${primary.name}`);
      setConfirmOpen(false);
      setPrimaryId("");
      setSecondaryId("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setMerging(false);
    }
  };

  const roleLabel = (u) => (u ? ROLE_LABEL[u.role] || u.role : "");

  return (
    <div className="space-y-4" data-testid="user-merge-panel">
      <div className="rounded-2xl bg-gradient-to-br from-red-500/15 to-transparent border border-red-500/30 p-4">
        <div className="flex items-center gap-2 mb-1">
          <GitMerge size={20} weight="duotone" className="text-red-400" />
          <div className="font-display font-semibold">Fusionner deux utilisateurs</div>
        </div>
        <div className="text-[11px] text-white/60 leading-relaxed">
          Consolide deux comptes en un seul. Tous les chantiers, interventions, chat, notifs, congés,
          tickets SAV et pings GPS du <b className="text-red-400">secondaire</b> sont réattribués au
          <b className="text-emerald-400"> principal</b>, puis le compte secondaire est supprimé.
          <br />
          <span className="text-white/40">
            <Warning size={11} weight="fill" className="inline text-amber-400" /> Action irréversible.
            Les permissions du secondaire sont fusionnées (union) sur le principal.
          </span>
        </div>
      </div>

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1 flex items-center gap-1">
              <User size={11} weight="fill" /> Principal (gardé)
            </div>
            <SearchablePicker
              items={primaryOptions}
              value={primaryId}
              onChange={setPrimaryId}
              placeholder="Choisir le compte à conserver…"
              getSubtitle={(u) => `${roleLabel(u)}${u.email ? " · " + u.email : ""}`}
              testid="user-merge-primary"
            />
          </div>
          <div className="text-white/30 text-center pt-4">
            <ArrowRight size={20} weight="bold" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-red-400 mb-1 flex items-center gap-1">
              <Trash size={11} weight="fill" /> Secondaire (supprimé)
            </div>
            <SearchablePicker
              items={secondaryOptions}
              value={secondaryId}
              onChange={setSecondaryId}
              placeholder="Choisir le doublon à fusionner…"
              getSubtitle={(u) => `${roleLabel(u)}${u.email ? " · " + u.email : ""}`}
              testid="user-merge-secondary"
            />
          </div>
        </div>

        {sameUser && (
          <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-2 text-[11px] text-amber-300">
            <Warning size={12} weight="fill" className="inline mr-1" /> Choisis deux utilisateurs différents.
          </div>
        )}

        <div className="flex flex-col md:flex-row items-stretch gap-2">
          <UserCard user={primary} isPrimary={true} />
          <div className="hidden md:flex items-center justify-center text-white/30">
            <ArrowRight size={16} weight="bold" />
          </div>
          <UserCard user={secondary} isPrimary={false} />
        </div>

        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={!canMerge || merging}
          className="w-full h-11 bg-red-500 hover:bg-red-600 text-white font-semibold"
          data-testid="user-merge-execute"
        >
          <GitMerge size={16} weight="bold" /> Fusionner
        </Button>
      </div>

      {result && (
        <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-4" data-testid="user-merge-result">
          <div className="text-sm font-semibold text-emerald-400 mb-2">Dernière fusion réussie</div>
          <div className="text-xs text-white/60 mb-2">Références réattribuées :</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
            {Object.entries(result.reassigned || {})
              .filter(([, v]) => v > 0)
              .map(([k, v]) => (
                <div key={k} className="rounded-lg bg-black/40 p-2 text-center">
                  <div className="text-emerald-400 font-mono font-bold">{v}</div>
                  <div className="text-white/50 text-[9px] uppercase tracking-widest">{k.replaceAll("_", " ")}</div>
                </div>
              ))}
          </div>
          {Object.values(result.reassigned || {}).every((v) => v === 0) && (
            <div className="text-[11px] text-white/50 italic">
              Aucune référence à réattribuer (compte secondaire vide).
            </div>
          )}
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Warning size={20} weight="duotone" className="text-red-400" /> Confirmer la fusion
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-white/70">
              Le compte <b className="text-red-400">{secondary?.name}</b> ({roleLabel(secondary)}) sera{" "}
              <b>définitivement supprimé</b>. Tout son historique (interventions, chantiers, notifs, congés…)
              sera transféré vers <b className="text-emerald-400">{primary?.name}</b> ({roleLabel(primary)}).
            </div>
            <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-2 text-[11px] text-red-300">
              Cette action ne peut pas être annulée.
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => setConfirmOpen(false)}
                disabled={merging}
                className="flex-1 border border-white/10"
              >
                Annuler
              </Button>
              <Button
                onClick={doMerge}
                disabled={merging}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold"
                data-testid="user-merge-confirm"
              >
                {merging ? "Fusion en cours…" : "Confirmer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
